import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { ApiError, authApi, NetworkError } from '../auth/api.ts';
import { AuthLayout, FormError } from '../auth/AuthLayout.tsx';
import { errorCopy, OFFLINE_COPY } from '../auth/errorCopy.ts';
import { Button, Field, TextInput } from '../components/index.ts';

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
});

type Values = z.infer<typeof schema>;

export function ForgotPasswordRoute() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.requestReset(values.email);
      setSent(true);
    } catch (error) {
      setFormError(
        error instanceof NetworkError
          ? OFFLINE_COPY
          : errorCopy(error instanceof ApiError ? error.code : null),
      );
    }
  });

  /*
   * The confirmation deliberately does not say whether an account exists. The
   * server answers identically either way, and saying "we've sent you an email"
   * only when the address is registered would give the whole thing away.
   */
  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        footer={
          <Link to="/login" className="text-accent underline hover:text-accent-hover">
            Back to sign in
          </Link>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-muted">
          <p>
            If that address has an ICULogbook account, a reset link is on its way. It works once and
            expires in an hour.
          </p>
          <p>Nothing arrived? Check the spam folder, then try again.</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to set a new one."
      footer={
        <Link to="/login" className="text-accent underline hover:text-accent-hover">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={formError} />

        <Field label="Email" error={errors.email?.message}>
          <TextInput
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            {...register('email')}
          />
        </Field>

        <Button type="submit" variant="primary" block loading={isSubmitting}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
